import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import {
  catchError,
  filter,
  finalize,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';

import { BaseService } from './base.service';
import { AppSettings } from '../app.settings';
import { Response } from '../models/response.model';
import { Order } from '../models/order.model';
import { Product } from '../models/product.model';
import { ProductCatalog } from '../models/product-catalog.model';
import { FilterGroup } from '../models/filter-group.model';
import { ProductService } from './product.service';
import {
  escapeRegExp,
  isEmptyArray,
  objectDateToString,
  transformFilters,
} from '../shared/utils';
import { getFilterMinMaxPrices } from '../shared/helpers';
import { GoogleAnalytics } from './google-analytics.service';
import { AutoShipService } from './auto-ship.service';
import { AutoShipItem } from '../models/auto-ship.model';
import { OrderTypes } from '../enums/order-types';
import { OrderService } from './order.service';
import { OrderSuggestion } from '../interfaces/order-suggestion';
import { AuthService } from './auth.service';
import { PageService } from './page.service';
import { OrderHistory } from '../models/order-history.model';
import { AppDate } from '../models/date.model';
import { AvailableOrderWindow } from '../interfaces/order-window';

const DEFAULT_LIMIT = 20;
const MIN_SYMBOLS_TO_SEARCH = 3;

const ORDER_SEARCH_LIMIT = 10;

export interface OrderCategory {
  type: string;
  label: string;
  total: number;
  orders: OrderHistory[];
  selected: boolean;
  fetching: boolean;
  sorting: boolean;
}

export interface SearchParam {
  name: string;
  label: string;
  value: string;
  selected: boolean;
}

export interface SearchResult {
  products: Product[] | AutoShipItem[];
  count: number;
  // entries in the search response itself; `products` can be empty although there were hits
  // (a swallowed product-loading error), and X-Count is 0 for a discontinued hit
  hits?: number;
  // set only by the error fallback, so a failed request is not mistaken for zero results
  failed?: boolean;
}

export interface SearchOptions {
  limit?: number;
  offset?: number;
  autoShip?: boolean;
  /** AB#48302: send the search-results sidebar selection. Opt-in, so unrelated callers of this
   *  root-provided service (quick order, autoship lookup) are never narrowed by that page's state. */
  useSearchFilters?: boolean;
  loadMore?: boolean;
  refresh?: boolean;
  entity_id?: boolean;
  localSearch?: boolean;
}
export const ALL_SEARCH_OPTIONS: SearchParam[] = [
  { name: 'order_id', label: 'Order #', value: '', selected: false },
  { name: 'sku_number', label: 'Product #', value: '', selected: false },
  { name: 'product_name', label: 'Product Name', value: '', selected: false },
  { name: 'window_id', label: 'Window Name', value: '', selected: false },
  { name: 'purchaser', label: 'Purchaser', value: '', selected: false },
  { name: 'cart_name', label: 'Order Name', value: '', selected: false },
  { name: 'sap', label: 'SAP #', value: '', selected: false },
  { name: 'tracking', label: 'Tracking #', value: '', selected: false },
];

const ORDER_HISTORY_TYPES = {
  PREORDER_TYPE: 'OW',
  ECOMMERCE_TYPE: 'OD',
  SUMMARY_TYPE: 'AB',
  BRANCH_TYPE: 'BR',
  CUSTOM_TYPE: 'AS',
};

export enum StateChange {
  All,
  SearchingProducts,
  OnUpdateSearchText,
}

export class State {
  eventChange: StateChange;
  searchingProducts: boolean;
  onUpdateSearchText: boolean;
}

@Injectable()
export class SearchService extends BaseService {
  private stateSubject: BehaviorSubject<State> = new BehaviorSubject<State>({
    eventChange: StateChange.All,
    searchingProducts: false,
    onUpdateSearchText: false,
  });

  state$ = this.stateSubject.asObservable();


  searchParams: SearchParam[] = [];
  selectedSearchParam: any;

  searchingOrders = false;
  orderCategories: OrderCategory[] = [];
  recentOnDemandOrders: OrderHistory[] = [];

  globalTerm: string;
  currentBuyingWindowId = 0;
  selectedOrderType: string;

  // AB#48302: sidebar filter groups for the active search-results tab. Deliberately kept here rather
  // than in PageService, whose cache is keyed by URL only and would leak facets between search terms.
  private searchFiltersByKey = new Map<string, FilterGroup[]>();
  private searchFiltersTerm = '';
  private loadingSearchFiltersKey: string | null = null;
  /** Identifies the request that owns `loadingSearchFiltersKey`; two requests can share a key. */
  private loadingSearchFiltersId = 0;
  private searchFiltersRequestId = 0;
  /** Newest request issued for each key, so an overtaken response cannot win the race. */
  private latestSearchFiltersRequestByKey = new Map<string, number>();
  /** Bumped every time the stored sets are dropped, so a request that was in flight across the
   *  drop cannot re-seed the map. The term alone cannot tell this apart: clearing the box and
   *  retyping the same term (or A -> B -> A) leaves `globalTerm` at its original value. */
  private searchFiltersGeneration = 0;
  /** Stable reference so an absent entry does not hand the sidebar a new array on every check. */
  private readonly noSearchFilters: FilterGroup[] = [];

  /** Facets (with their selection) for the tab the user is looking at. */
  public get searchFilters(): FilterGroup[] {
    return (
      this.searchFiltersByKey.get(this.currentSearchFiltersKey) ??
      this.noSearchFilters
    );
  }

  foundProducts: Product[] = [];
  discontinuedProductId = 0;
  discontinuedHasReplacements = false;

  totalProducts = 0;
  sortedColumn = { column: 'order_id', direction: 'DESC' };

  initialOrderSearchPerformed = false;
  private searchOffset = 0;

  get state(): State {
    return this.stateSubject.value;
  }

  setState(state: Partial<State>): void {
    this.stateSubject.next({
      ...this.state,
      ...state,
    });
  }

  get canLoadMore(): boolean {
    if (this.state.searchingProducts) {
      return false;
    }
    return this.totalProducts > this.foundProducts.length;
  }


  constructor(
    protected http: HttpClient,
    protected appSettings: AppSettings,
    protected toastr: ToastrService,
    private productService: ProductService,
    private orderService: OrderService,
    private autoShipService: AutoShipService,
    private googleAnalytics: GoogleAnalytics,
    private authService: AuthService,
    private pageService: PageService
  ) {
    super('/search', http, appSettings, toastr);
    this.initOrderSearch();
  }

  setFoundProducts(val: Product[], append = false) {
    if (!val) {
      return;
    }
    if (append) {
      this.foundProducts = [...this.foundProducts, ...val];
    } else {
      this.foundProducts = val;
    }
  }

  public get withoutDiscontinued(): Product[] {
    return this.foundProducts.filter((p) => p.obsolete !== true);
  }

  public productsSearch(options: SearchOptions = {}): Observable<SearchResult> {
    if (!this.globalTerm || this.globalTerm.length < MIN_SYMBOLS_TO_SEARCH) {
      return of({ products: [], count: 0 });
    }

    this.totalProducts = 0;
    if (!options.loadMore && !options.localSearch) {
      this.foundProducts = [];
      this.searchOffset = 0;
    }

    // AB#48302: facets are stored per term + window, so switching tabs or windows picks up that
    // tab's own selection (and never the other one's). A new term drops every stored set.
    this.forgetSearchFiltersOfOtherTerms();
    const params = {
      match: this.globalTerm,
      window_id: this.currentBuyingWindowId,
    };
    if (options.autoShip) {
      params['autoship'] = true;
    } else if (options.useSearchFilters) {
      // AB#48302: attribute filters apply to products only, never to autoship hits
      Object.assign(params, this.selectedFilterPayload());
    }

    if (options.entity_id) {
      params['entity_id'] = options.entity_id;
    }

    let limit = options.limit || DEFAULT_LIMIT;
    let offset = options.offset || 0;
    if (options.loadMore) {
      offset = this.searchOffset;
    }

    if (options.refresh) {
      limit = this.foundProducts.length;
      offset = 0;
    }

    if (!options.localSearch) {
      this.setState({
        eventChange: StateChange.SearchingProducts,
        searchingProducts: true,
      });
    }

    // reset discontinued product id if any
    this.discontinuedProductId = 0;
    this.discontinuedHasReplacements = false;

    return this.http
      .post<
        HttpResponse<Response>
      >(this.apiURL, params, { observe: 'response', headers: new HttpHeaders({ Range: `${limit}-${offset}`, 'x-xsrf-token': '' }) })
      .pipe(
        switchMap((httpResponse) => {
          const count = parseInt(httpResponse.headers.get('X-Count'), 10);

          this.totalProducts = count;
          this.searchOffset += DEFAULT_LIMIT;
          const response = new Response(httpResponse.body);
          const hits = this.toSearchEntries(response.data).length;
          // this.foundProductIdCache.set(key, {count, ids: response.data});
          return this.fetchProductsAndAutoships(response.data, options).pipe(
            map((result) => {
              return {
                products: result as SearchResult['products'],
                count: count,
                hits: hits,
              };
            })
          );
        }),
        catchError(
          this.handleError('SearchService::productsSearch', {
            products: [],
            count: 0,
            failed: true,
          })
        ),
        tap(() => {
          if (!options.localSearch) {
            this.setState({
              eventChange: StateChange.SearchingProducts,
              searchingProducts: false,
            });
          }
        })
      );
  }

  /**
   * AB#48302: filter facets for the current search term and tab, narrowed by the current selection
   * exactly as the product pages narrow theirs. Not cached — the facets depend on the search term.
   */
  public getSearchFilters(
    filters: FilterGroup[] = this.searchFilters
  ): Observable<FilterGroup[]> {
    if (!this.globalTerm || this.globalTerm.length < MIN_SYMBOLS_TO_SEARCH) {
      // keep the remembered term in step with the map it describes, or the next search on the term
      // we just dropped would believe its sets are still stored
      this.dropSearchFilters();
      return of([]);
    }

    // facets can be requested without going through productsSearch(), so guard the term here too
    this.forgetSearchFiltersOfOtherTerms();

    const body = {
      match: this.globalTerm,
      window_id: this.currentBuyingWindowId,
      ...this.selectedFilterPayload(filters),
    };

    // The key is captured here, not read again when the response lands: a tab or window switch while
    // this request is in flight must not stamp these facets as the new search's.
    const requestedKey = this.currentSearchFiltersKey;
    const requestedTerm = this.globalTerm;
    const requestedGeneration = this.searchFiltersGeneration;
    const requestId = ++this.searchFiltersRequestId;
    this.loadingSearchFiltersKey = requestedKey;
    this.loadingSearchFiltersId = requestId;
    this.latestSearchFiltersRequestByKey.set(requestedKey, requestId);

    return this.http
      .post<Response>(this.apiURL + '/product-filters', body, {
        headers: new HttpHeaders({ 'x-xsrf-token': '' }),
      })
      .pipe(
        // A stale response is dropped from the stream rather than emitted: filterProducts() re-runs
        // the search on every emission, so emitting here would fire a second search reading the
        // selection stored before this request — racing the one the newer response will start.
        filter(
          () =>
            !this.isStaleFacetsResponse(
              requestedTerm,
              requestedGeneration,
              requestedKey,
              requestId
            )
        ),
        map((response) => {
          const result = new Response(response).data || [];
          this.searchFiltersByKey.set(
            requestedKey,
            result.map((item) => new FilterGroup(item))
          );
          return this.searchFilters;
        }),
        catchError(this.handleError('SearchService::getSearchFilters', [])),
        finalize(() => {
          // only clear our own marker — a late response must not release a newer request's guard,
          // and the key alone does not identify it: the newer request can carry the same key
          if (this.loadingSearchFiltersId === requestId) {
            this.loadingSearchFiltersKey = null;
          }
        })
      );
  }

  /**
   * AB#48302: loads the facets only when the current search has none yet.
   *
   * The search-results page re-creates its component on every visit, so an unconditional fetch would
   * come back with an unselected facet set and wipe a selection that is still applied to the grid
   * (e.g. after returning from a product with the browser Back button). The facets outlive the
   * component here, so the decision belongs to the service.
   */
  public ensureSearchFilters(): Observable<FilterGroup[]> {
    if (
      this.hasFiltersForCurrentSearch ||
      this.loadingSearchFiltersKey === this.currentSearchFiltersKey
    ) {
      return of(this.searchFilters);
    }

    return this.getSearchFilters([]);
  }

  /**
   * Whether a facets response no longer describes the search the user is looking at.
   *
   * A tab switch does NOT make one stale: the sets are stored per tab, so a response arriving after
   * the switch still belongs under its own key and must be kept, or the selection that tab is
   * filtered by would be lost. Three things do:
   *  - the search term changed;
   *  - the stored sets were dropped while this was in flight (a cleared search box, A -> B -> A);
   *  - a newer request for the same tab has since been issued (two quick filter applies).
   */
  private isStaleFacetsResponse(
    requestedTerm: string,
    requestedGeneration: number,
    requestedKey: string,
    requestId: number
  ): boolean {
    return (
      requestedTerm !== this.globalTerm ||
      requestedGeneration !== this.searchFiltersGeneration ||
      this.latestSearchFiltersRequestByKey.get(requestedKey) !== requestId
    );
  }

  /** True when facets for the current term + window have already been loaded. */
  public get hasFiltersForCurrentSearch(): boolean {
    // presence, not emptiness: a search that legitimately has no facets is still loaded, and
    // re-asking for it on every search event (including every load-more) is pure waste. A failed
    // fetch stores nothing, so it still retries.
    return this.searchFiltersByKey.has(this.currentSearchFiltersKey);
  }

  /**
   * Facets are remembered per tab for the duration of one search term. As soon as the term changes
   * the stored sets are meaningless — they describe a different result set — so they are dropped.
   */
  private forgetSearchFiltersOfOtherTerms(): void {
    if (this.searchFiltersTerm !== this.globalTerm) {
      this.dropSearchFilters(this.globalTerm);
    }
  }

  /**
   * Forgets every stored set. Bumping the generation marks any request still in flight as stale, so
   * its response cannot restore a selection the user has just dropped.
   */
  private dropSearchFilters(term = ''): void {
    this.searchFiltersByKey.clear();
    this.latestSearchFiltersRequestByKey.clear();
    this.searchFiltersTerm = term;
    this.searchFiltersGeneration++;
  }

  public get hasSelectedSearchFilters(): boolean {
    return this.selectedFilterIds(this.searchFilters).length > 0;
  }

  /** The search the current `searchFilters` belong to. */
  private get currentSearchFiltersKey(): string {
    return `${this.globalTerm}|${this.currentBuyingWindowId}`;
  }

  private selectedFilterIds(filters: FilterGroup[]): string[] {
    return isEmptyArray(filters) ? [] : transformFilters(filters);
  }

  /** `filter` / `price` in the shape both /search and /search/product-filters expect. */
  private selectedFilterPayload(
    filters: FilterGroup[] = this.searchFilters
  ): { filter?: string[]; price?: { min_price: number; max_price: number } } {
    const payload = {};
    const selected = this.selectedFilterIds(filters);
    if (selected.length > 0) {
      payload['filter'] = selected;
    }

    const minMaxPriceFilter = getFilterMinMaxPrices(filters);
    if (minMaxPriceFilter) {
      payload['price'] = {
        min_price: minMaxPriceFilter.minPrice,
        max_price: minMaxPriceFilter.maxPrice,
      };
    }

    return payload;
  }

  // the search response carries either a list of entries or a single entry object
  private toSearchEntries(data): any[] {
    if (!isEmptyArray(data)) {
      return data;
    }

    return data?.id ? [{ ...data }] : [];
  }

  private fetchProductsAndAutoships(
    data,
    options: SearchOptions = {}
  ): Observable<(Product | AutoShipItem)[]> {
    const searchIds = this.toSearchEntries(data);

    if (!isEmptyArray(searchIds)) {
      const discontinuedProducts = searchIds.filter(
        (i) => i.type === 'discontinued'
      );
      if (discontinuedProducts.length === 1) {
        // discontinued product should appear only for exact match and be only 1, otherwise ignore them
        this.discontinuedProductId = discontinuedProducts[0].id;

        // check if discontinued product has a replacement
        const replacement = discontinuedProducts[0].replacement;
        if (replacement) {
          return this.getProductsByIds(
            [this.discontinuedProductId, replacement.id],
            { loadMore: false }
          ).pipe(
            map((products) => {
              // set
              return products.map((p) => {
                if (p.id === replacement.id) {
                  p.productReplacement = true;
                } else if (p.id === this.discontinuedProductId) {
                  p.obsolete = true;
                }
                return p;
              });
            }),
            tap((products) => {
              if (!isEmptyArray(products)) {
                this.discontinuedHasReplacements = true;
              }
            })
          );
        } else {
          return this.getProductsByIds([this.discontinuedProductId], {
            loadMore: false,
          }).pipe(
            map((products) => {
              return products.map((p) => {
                p.obsolete = true;
                return p;
              });
            }),
            tap((products) => {
              if (!isEmptyArray(products)) {
                this.discontinuedHasReplacements = false;
              }
            })
          );
        }
      }

      const productsIds = searchIds
        .filter((i) => i.type === 'product')
        .map((i) => i.id);
      const autoShipIds = options.autoShip
        ? searchIds.filter((i) => i.type === 'autoship').map((i) => i.id)
        : [];

      return forkJoin([
        this.getProductsByIds(productsIds, options),
        this.getAutoShipsByIds(autoShipIds),
      ]).pipe(
        map(([products, autoships]) => {
          const result = [];
          searchIds.forEach((i) => {
            if (i.type === 'product') {
              const product = products.find((p) => p.id === i.id);
              if (product) {
                result.push(product);
              }
            }
            if (options.autoShip && i.type === 'autoship') {
              const autoship = autoships.find((a) => a.id === i.id);
              if (autoship) {
                result.push(autoship);
              }
            }
          });

          return result;
        })
      );
    }

    return of([]);
  }

  private getProductsByIds(
    ids: number[],
    options: SearchOptions = {}
  ): Observable<Product[]> {
    return this.productService.getProductsByIds(ids, true).pipe(
      tap((products) => {
        if (Array.isArray(products) && products.length > 0) {
          this.googleAnalytics.viewSearchResults(this.globalTerm, products);
        }
        if (!options.localSearch) {
          this.setFoundProducts(products, options.loadMore);
        }
      })
    );
  }

  private getAutoShipsByIds(ids: number[]): Observable<AutoShipItem[]> {
    return this.autoShipService.fetchByIds(ids);
  }

  highlight(str) {
    if (!this.globalTerm) {
      return;
    }

    const safeStr = str.replace(/&amp;/gm, '&');
    const searchPattern = escapeRegExp(this.globalTerm); // escape special characters
    const pattern = new RegExp('(' + searchPattern + ')', 'gi');
    return safeStr.replace(pattern, (match) => `<strong>${match}</strong>`);
  }

  orderSuggestions(
    term: string,
    filter: any = null
  ): Observable<OrderSuggestion> {
    const resultKey: string = btoa(
      JSON.stringify({ k: term, f: filter })
    ).replace('=', '');

    if (this.cache.has(resultKey)) {
      return of(this.cache.get(resultKey));
    }

    return this.http
      .post<Response>(
        this.apiURL + '/order-suggestions',
        { term: term, filter: filter },
        { headers: this.xsrfTokenHeader }
      )
      .pipe(
        map((response) => {
          const result = response.data;

          this.cache.set(resultKey, result);

          return result;
        }),
        catchError(this.handleError('SearchService::orderSuggestions', {}))
      );
  }

  orderResults(
    key: string,
    filter: any = null,
    paging: string = '18-0'
  ): Observable<any> {
    return this.results('order', key, filter, paging);
  }

  results(
    type: string,
    key: string,
    filter: any = null,
    paging: string = '18-0'
  ): Observable<any> {
    const filterKey: string = btoa(JSON.stringify({ t: type, k: key })).replace(
      '=',
      ''
    );
    const resultKey: string = btoa(
      JSON.stringify({ t: type, k: key, f: filter, p: paging })
    ).replace('=', '');
    const eTag: string = this.cache.getETag(filterKey);
    const headers: HttpHeaders = new HttpHeaders({
      Range: paging,
      'If-Match': eTag,
    });
    const data = { key: key, filter: filter };

    if (this.cache.has(resultKey)) {
      return of(this.cache.get(resultKey));
    }

    switch (type) {
      case 'order':
        return this.getOrders(resultKey, data, headers);
    }
  }

  getCatalogs(
    key: string,
    filterKey: string,
    data: any,
    headers: HttpHeaders
  ): Observable<any[]> {
    return this.http
      .post<
        HttpResponse<Response>
      >(this.apiURL + '/program/results', data, { observe: 'response', headers: headers })
      .pipe(
        map((httpResponse) => {
          const response = new Response(httpResponse.body);
          const result = response.data;

          this.cacheFilters(filterKey, result);

          this.productService.catalogCache.set(key, _.clone(result.items));

          _.each(result.items, (id, i) => {
            if (this.productService.catalogCache.has(id)) {
              result.items[i] = new ProductCatalog(
                this.productService.catalogCache.get(id)
              );
            }
          });

          return result.items;
        }),
        catchError(this.handleError('SearchService::getCatalogs', []))
      );
  }

  getProducts(
    type: string,
    key: string,
    filterKey: string,
    data: any,
    headers: HttpHeaders
  ): Observable<Product[]> {
    return this.http
      .post<
        HttpResponse<Response>
      >(this.apiURL + '/' + type + '/results', data, { observe: 'response', headers: headers })
      .pipe(
        map((httpResponse) => {
          const response = new Response(httpResponse.body);
          const result = response.data;

          this.cacheFilters(filterKey, result);

          this.productService.productCache.set(key, _.clone(result.items));

          _.each(result.items, (id, i) => {
            if (this.productService.productCache.has(id)) {
              result.items[i] = new Product(
                this.productService.productCache.get(id)
              );
            }
          });

          return result.items;
        }),
        catchError(this.handleError('SearchService::getProducts', []))
      );
  }

  getOrders(key: string, data: any, headers: HttpHeaders): Observable<any[]> {
    return this.http
      .post<
        HttpResponse<Response>
      >(this.apiURL + '/order/results', data, { observe: 'response', headers: headers })
      .pipe(
        map((httpResponse) => {
          const response = new Response(httpResponse.body);
          const result = response.data;

          _.each(result, (order, i) => {
            result[i] = new Order(order);
          });

          return result;
        }),
        catchError(this.handleError('SearchService::getOrders', []))
      );
  }

  cacheResult(
    resultKey: string,
    filterKey: string,
    result: any
  ): Observable<any> {
    const out = { items: result, filters: this.cache.get(filterKey) };
    this.cache.set(resultKey, out);
    return of(out);
  }

  cacheFilters(key: string, data: any) {
    const eTag: string = this.cache.getETag(key);

    if (eTag !== data.eTag) {
      _.each(data.filters, (item, i) => {
        data.filters[i] = new FilterGroup(item);
      });
      this.cache.setETag(key, data.eTag);
      this.cache.set(key, data.filters);
    }
  }

  public clearSearchResults() {
    this.globalTerm = '';
    this.foundProducts = [];
    this.totalProducts = 0;
    this.searchOffset = 0;
    this.dropSearchFilters();
    this.loadingSearchFiltersKey = null;
  }

  public get hasProducts(): boolean {
    return this.foundProducts && this.foundProducts.length > 0;
  }

  public setCurrentOrderWindowId(val: number) {
    this.currentBuyingWindowId = val;
    this.selectedOrderType =
      this.currentBuyingWindowId > 0
        ? OrderTypes.PREORDER_TYPE
        : OrderTypes.ECOMMERCE_TYPE;
  }

  private initOrderSearch() {
    this.sortedColumn = { column: 'order_id', direction: 'DESC' };
    this.selectedSearchParam = null;
    this.searchParams = ALL_SEARCH_OPTIONS.filter((o) => {
      // remove sap from search options if user can't have access to it
      if (!this.authService.canEditSAP && o.name === 'sap') {
        return false;
      }

      // remove window from search options if user can't have access to it
      if (!this.authService.canBuyingWindow && o.name === 'window_id') {
        return false;
      }

      return true;
    });

    this.orderCategories = [];

    if (this.authService.canBuyingWindow) {
      this.orderCategories.push({
        type: OrderTypes.PREORDER_TYPE,
        label: 'Buying Window',
        total: 0,
        orders: [],
        fetching: false,
        selected: false,
        sorting: false,
      });
    }

    if (
      this.authService.isWholeSaler ||
      this.authService.isWholeSalerMultiple
    ) {
      this.orderCategories.push({
        type: OrderTypes.SUMMARY_TYPE,
        total: 0,
        label: 'AB Orders',
        orders: [],
        fetching: false,
        selected: false,
        sorting: false,
      });
    }

    if (this.authService.isWholeSalerMultiple) {
      this.orderCategories.push({
        type: OrderTypes.BRANCH_TYPE,
        total: 0,
        label: 'Branch Orders',
        orders: [],
        fetching: false,
        selected: false,
        sorting: false,
      });
    }

    this.orderCategories.push({
      type: OrderTypes.ECOMMERCE_TYPE,
      total: 0,
      label: 'On Demand',
      orders: [],
      fetching: false,
      selected: false,
      sorting: false,
    });

    if (this.authService.canCustomRequest) {
      this.orderCategories.push({
        type: OrderTypes.CUSTOM_TYPE,
        total: 0,
        label: 'Custom Request',
        orders: [],
        fetching: false,
        selected: false,
        sorting: false,
      });
    }

    const selectedTab = this.pageService.getSelectedOrderHistoryTab();
    if (selectedTab) {
      this.orderCategories.forEach((s) => {
        s.selected = s.type === selectedTab;
      });
    }

    // check if one of category is selected
    if (this.orderCategories.filter((oc) => oc.selected).length === 0) {
      this.orderCategories[0].selected = true;
    }
  }

  private getFilters(type: string, force = false) {
    const filter = { type: ORDER_HISTORY_TYPES[type] };
    if (this.selectedSearchParam && !force) {
      filter['search_params'] = this.selectedSearchParam;
    }

    if (this.sortedColumn.column) {
      filter['sort'] = {
        column: this.sortedColumn.column,
        direction: this.sortedColumn.direction,
      };
    }
    return filter;
  }

  public searchOrders(searchParams: any = null) {
    if (!searchParams) {
      // search by default on init page
    } else {
      this.selectedSearchParam = searchParams;
    }

    this.searchingOrders = true;
    forkJoin(
      this.orderCategories.map((s) => {
        const paging = `${ORDER_SEARCH_LIMIT}-${0}`;
        return this.orderService.getOrderHistory(
          this.getFilters(s.type),
          paging
        );
      })
    ).subscribe((resultList) => {
      const searchResults = [...this.orderCategories];
      resultList.forEach((result) => {
        const searchResult = searchResults.find(
          (s) => s.type === this.getMappedOrderHistoryType(result.type)
        );
        if (searchResult) {
          searchResult.orders = result.orders;
          searchResult.total = result.count;
        }
      });

      this.searchingOrders = false;
      this.initialOrderSearchPerformed = true;
    });
  }

  public searchRecentODOrders() {
    this.searchingOrders = true;
    const paging = `${ORDER_SEARCH_LIMIT}-${0}`;
    this.recentOnDemandOrders = [];

    const filters = this.getFilters(OrderTypes.ECOMMERCE_TYPE, true);
    filters['user_only'] = true;

    return this.orderService
      .getOrderHistory(filters, paging)
      .subscribe((resultList) => {
        if (!isEmptyArray(resultList?.orders)) {
          this.recentOnDemandOrders = resultList.orders;
        }
        this.recentOnDemandOrders = resultList.orders;
        this.searchingOrders = false;
      });
  }

  loadMore(type: string) {
    const ordersType = this.orderCategories.find((s) => s.type === type);
    if (!ordersType) {
      return;
    }

    if (ordersType.total === ordersType.orders.length) {
      return;
    }

    const paging = `${ORDER_SEARCH_LIMIT}-${ordersType.orders.length}`;
    ordersType.fetching = true;

    this.orderService
      .getOrderHistory(this.getFilters(ordersType.type), paging)
      .subscribe((result) => {
        this.orderCategories = this.orderCategories.map((s) => {
          s.fetching = false;
          const returnedType = this.getMappedOrderHistoryType(result.type);
          if (s.type !== returnedType) {
            return s;
          }

          s.orders = [...s.orders, ...result.orders];
          return s;
        });
      });
  }

  public sortOrders(column: string) {
    const direction = this.sortedColumn.direction === 'ASC' ? 'DESC' : 'ASC';
    this.sortedColumn = { column, direction };

    forkJoin(
      this.orderCategories.map((oc) => {
        return this.sort(oc);
      })
    ).subscribe((result) => {
      this.orderCategories = result.map((r) => {
        r.sorting = false;
        return r;
      });
    });
  }

  public exportToExcel(type: string) {
    const params = btoa(JSON.stringify(this.getFilters(type)));
    window.open(
      `${this.orderService.getApiUrl()}s/spreadsheet?params=${params}`,
      '_blank'
    );
  }

  public setGlobalTerm(term: string) {
    this.globalTerm = term;
  }

  private getOrderValueByColumnsName(order: OrderHistory, columnName: string) {
    switch (columnName) {
      case 'order_id':
        return order.id;
      case 'state':
        return order.state;
      case 'order_date':
        return order.order_date;
      case 'purchaser':
        return order.purchaser;
      case 'cart_name':
        return order.cart_name;
      case 'items_count':
        return order.items_count;
      case 'order_total':
        return order.order_total;
      default:
        return null;
    }
  }

  private internalSort(
    order1: OrderHistory,
    order2: OrderHistory,
    columnName: string,
    dir: string
  ): number {
    const value1 = this.getOrderValueByColumnsName(order1, columnName);
    const value2 = this.getOrderValueByColumnsName(order2, columnName);

    if (typeof value1 === 'string' && typeof value2 === 'string') {
      return dir === 'ASC'
        ? value1.localeCompare(value2)
        : value2.localeCompare(value1);
    }

    if (typeof value1 === 'number' && typeof value2 === 'number') {
      return dir === 'ASC' ? value1 - value2 : value2 - value1;
    }

    if (columnName === 'order_date') {
      if (!value1 && !value2) {
        return 0;
      }

      if (dir === 'ASC') {
        if (!value1) {
          return -1;
        }
        if (!value2) {
          return 1;
        }
        return (value1 as AppDate).isBefore(value2) ? -1 : 1;
      } else {
        if (!value2) {
          return -1;
        }
        if (!value1) {
          return 1;
        }
        return (value2 as AppDate).isBefore(value1) ? -1 : 1;
      }
    }

    return 0;
  }

  private sort(orderCategory: OrderCategory): Observable<OrderCategory> {
    if (orderCategory.total > orderCategory.orders.length) {
      // sorting on server
      const paging = `${orderCategory.orders.length}-0`;
      orderCategory.fetching = true;

      this.orderService
        .getOrderHistory(this.getFilters(orderCategory.type), paging)
        .subscribe((result) => {
          this.orderCategories = this.orderCategories.map((s) => {
            s.fetching = false;
            const returnedType = this.getMappedOrderHistoryType(result.type);
            if (s.type !== returnedType) {
              return s;
            }
            s.orders = result.orders;
            return s;
          });
        });
    } else {
      // internal sorting
      const orders = orderCategory.orders.sort((o1, o2) => {
        return this.internalSort(
          o1,
          o2,
          this.sortedColumn.column,
          this.sortedColumn.direction
        );
      });

      orderCategory.orders = orders;
    }
    return of(orderCategory);
  }

  public selectedOrdersType(type: string) {
    this.pageService.setSelectedOrderHistoryTab(type);
    this.orderCategories = this.orderCategories.map((s) => {
      return { ...s, selected: s.type === type };
    });
  }

  public getBuyingWindows(): Observable<AvailableOrderWindow[]> {
    return this.orderService.getAvailableOrderWindows();
  }

  public resetSearch() {
    this.initOrderSearch();
    this.searchOrders();
  }

  public get selectedOrderCategory() {
    return this.orderCategories.find((oc) => oc.selected);
  }

  public get onDemandOrdersHistory() {
    return (
      this.orderCategories.find(
        (category) => category.type === OrderTypes.ECOMMERCE_TYPE
      )?.orders || []
    );
  }

  get selectedOrders(): OrderHistory[] {
    return this.selectedOrderCategory?.orders || [];
  }

  private getMappedOrderHistoryType(type: string): string {
    for (const [key, value] of Object.entries(ORDER_HISTORY_TYPES)) {
      if (value === type) {
        return key;
      }
    }
  }

  setOnUpdateSearchText(val: boolean): void {
    this.setState({
      eventChange: StateChange.OnUpdateSearchText,
      onUpdateSearchText: val,
    });
  }

  shouldUpdateSearchTextChanged(): Observable<boolean> {
    // return this.toRx('onUpdateSearchText');
    return this.state$.pipe(
      filter((e) =>
        [StateChange.OnUpdateSearchText, StateChange.All].includes(
          e.eventChange
        )
      ),
      map((s) => s.onUpdateSearchText)
    );
  }
}
